import React from 'react';
import ReactDOM from 'react-dom/client';
import { Route, createBrowserRouter, createRoutesFromElements, RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import store from './store';
import 'bootstrap/dist/css/bootstrap.min.css';
import './assets/styles/bootstrap.custom.css';
import './assets/styles/index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import PrivateRoute from './Components/PrivateRoute';
import AdminRoute from './Components/AdminRoute';
import AllProductsScreen from './screens/AllProductsScreen';
import ProductScreen from './screens/ProductScreen';
import CartScreen from './screens/CartScreen';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import ShippingScreen from './screens/ShippingScreen';
import PaymentScreen from './screens/PaymentScreen';
import PlaceOrderScreen from './screens/PlaceOrderScreen';
import OrderScreen from './screens/OrderScreen';
import ProfileScreen from './screens/ProfileScreen';
import OrderListScreen from './screens/admin/OrderListScreen';
import ProductListScreen from './screens/admin/ProductListScreen';
import ProductEditScreen from './screens/admin/ProductEditScreen';
import UserListScreen from './screens/admin/UserListScreen';
import UserEditScreen from './screens/admin/UserEditScreen';
import VehicleMakes from './screens/VehicleMakes';
import MakeAdminScreen from "./screens/admin/MakeAdminScreen";
import FitmentCatalogAdminScreen from "./screens/admin/FitmentCatalogAdminScreen";
import VehicleSelectorScreen from './screens/VehicleSelectorScreen';
import BrowsePartsScreen from './screens/BrowsePartsScreen';
import ResultsScreen from './screens/ResultsScreen';
import PartCategoryAdminScreen from './screens/admin/PartCategoryAdminScreen.jsx';
import AdminProductsHub from './screens/admin/AdminProductsHub';  
import ContactScreen from './screens/ContactScreen'; 
import AboutScreen from './screens/AboutScreen';
import QuoteListScreen from './screens/admin/QuoteListScreen.jsx';
import QuoteEditScreen from './screens/admin/QuoteEditScreen.jsx';
import QuoteConvertScreen from './screens/admin/QuoteConvertScreen.jsx';
import VerifyEmailScreen from './screens/VerifyEmailScreen.jsx';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen.jsx';
import ResetPasswordScreen from './screens/ResetPasswordScreen.jsx';
import GuestOrderSuccess from './screens/GuestOrderSuccess.jsx';
import ReturnPolicyScreen from './screens/ReturnPolicyScreen';
import ShippingPolicyScreen from './screens/ShippingPolicyScreen';
import ReturnRequestScreen from './screens/ReturnRequestScreen';
import ReturnListScreen from './screens/admin/ReturnListScreen';
import PartnerCodesScreen from './screens/admin/PartnerCodesScreen';
import PartnerRedemptionsScreen from './screens/admin/PartnerRedemptionsScreen';
import BulkOrderScreen from './screens/BulkOrderScreen';
import VerifyEmployeeScreen from './screens/VerifyEmployeeScreen';
import BarcodeLookupScreen from './screens/admin/BarcodeLookupScreen';
import EomBillingScreen from './screens/admin/EomBillingScreen';
import EomStatementPayScreen from './screens/EomStatementPayScreen';
import RouterErrorScreen from './screens/RouterErrorScreen';
import EngineOilScreen from './screens/EngineOilScreen';

const isExtensionWalletNoise = (reason) => {
  const message = String(reason?.message || reason || '');
  const stack = String(reason?.stack || '');
  const text = `${message}\n${stack}`.toLowerCase();
  return (
    text.includes('failed to connect to metamask') ||
    (text.includes('chrome-extension://') && text.includes('inpage.js'))
  );
};

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (isExtensionWalletNoise(event?.reason)) {
      event.preventDefault();
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('Ignored browser extension wallet connection error:', event.reason);
      }
    }
  });
}

const router = createBrowserRouter(
    createRoutesFromElements(     
          <Route path="/" element={<App />} errorElement={<RouterErrorScreen />}>
            <Route index element={<BrowsePartsScreen />} />
            <Route path="browse" element={<BrowsePartsScreen />} />    
            <Route path="makes" element={<VehicleMakes />} />
            <Route path="engine-oil" element={<EngineOilScreen />} />
            <Route path="results" element={<ResultsScreen />} />
            <Route path="search" element={<ResultsScreen />} />
            <Route path='/search/:keyword' element={<AllProductsScreen />} />
            <Route path='/category/:categorySlug' element={<AllProductsScreen />} />
            <Route path='/page/:pageNumber' element={<AllProductsScreen />} />
            <Route path='search/:keyword/page/:pageNumber' element={<AllProductsScreen />} />
            <Route path="home" element={<AllProductsScreen />} />        
            <Route path="products" element={<AllProductsScreen />} />
            <Route path="product/:id" element={<ProductScreen />} />
            <Route path="cart" element={<CartScreen />} />
            <Route path='/login' element={<LoginScreen />} />
            <Route path='/verify-email' element={<VerifyEmailScreen />} />
            <Route path='/verify' element={<VerifyEmployeeScreen />} />
            <Route path='/forgot-password' element={<ForgotPasswordScreen />} />
            <Route path='/reset-password' element={<ResetPasswordScreen />} />
            <Route path='register' element={<RegisterScreen/>} />
            <Route path="/vehicles" element={<VehicleSelectorScreen />} />
            <Route path="contact" element={<ContactScreen />} />
            <Route path="about" element={<AboutScreen />} />
            <Route path="/guest-shipping" element={<ShippingScreen />} />
            <Route path="/guest-payment" element={<PaymentScreen />} />
            <Route path="/guest-placeorder" element={<PlaceOrderScreen />} />
            <Route path="/guest-order-success/:id" element={<GuestOrderSuccess />} />
            <Route path="/returns/policy" element={<ReturnPolicyScreen />} />
            <Route path="/shipping/policy" element={<ShippingPolicyScreen />} />
            <Route path="/returns" element={<ReturnRequestScreen />} />
            <Route path="/return-request" element={<ReturnRequestScreen />} />
            <Route path="/return-request/:orderId" element={<ReturnRequestScreen />} />

            <Route path='' element={<PrivateRoute/>}>
              <Route path='shipping' element={<ShippingScreen />} />
              <Route path='payment' element={<PaymentScreen />} />
              <Route path='placeorder' element={<PlaceOrderScreen />} />
               <Route path='/order/:id' element={<OrderScreen />} />
               <Route path='/profile' element={<ProfileScreen />} />
              <Route path='bulk-order' element={<BulkOrderScreen />} />
              <Route path='/eom-statement/:statementId/pay' element={<EomStatementPayScreen />} />
            </Route>

            <Route path='' element={<AdminRoute/>}>
              <Route path='admin/orderlist' element={<OrderListScreen />} />
              <Route path="/admin/products" element={<AdminProductsHub />} />
              <Route path="/admin/productlist" element={<ProductListScreen />} />
              <Route path='admin/productlist/:pageNumber' element={<ProductListScreen />} />
              <Route path='admin/product/:id/edit' element={<ProductEditScreen />} />
              <Route path='/admin/userlist' element={<UserListScreen />} />
              <Route path='/admin/user/:id/edit' element={<UserEditScreen />} />
              <Route path="admin/makes" element={<MakeAdminScreen />} />
              <Route path="/admin/fitments" element={<FitmentCatalogAdminScreen />} />
              <Route path="/admin/part-categories" element={<PartCategoryAdminScreen />} />
              <Route path="/admin/quotes" element={<QuoteListScreen />} />
              <Route path="/admin/quotes/:id/edit" element={<QuoteEditScreen />} />
              <Route path="/admin/quotes/:id/convert" element={<QuoteConvertScreen />} />
              <Route path="/admin/returns" element={<ReturnListScreen />} />
              <Route path="/admin/partner-codes" element={<PartnerCodesScreen />} />
              <Route path="/admin/partner-redemptions" element={<PartnerRedemptionsScreen />} />
              <Route path="/admin/barcode-lookup" element={<BarcodeLookupScreen />} />
              <Route path="/admin/eom-billing" element={<EomBillingScreen />} />
            </Route>
          </Route>
      )
    );
  

  const root = ReactDOM.createRoot(document.getElementById('root'));
      root.render(
            <React.StrictMode>
              <HelmetProvider>
                <Provider store={store}>
                  <RouterProvider router={router} />
                </Provider>
                </HelmetProvider>
            </React.StrictMode>
    );

reportWebVitals();
